<template>
  <div data-testid="artist-edits-info" class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-bold">Artist Edits - How it Works?</h2>
        <p class="text-sm text-gray-600">Human artist updates within 24 hours.</p>
      </div>
      <button
        v-if="isMobile"
        type="button"
        class="cursor-pointer self-start border-0 bg-transparent p-0"
        data-testid="artist-edits-info-close-button"
        @click="$emit('close')"
      >
        <UseSvg icon="menu-close" class="h-5 w-5" />
      </button>
    </div>

    <div class="relative flex rounded-xl border-[3px] border-gray-200 p-1.5">
      <!-- Arrow -->
      <div class="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
        <div class="flex size-8 items-center justify-center rounded-full bg-black shadow-md">
          <UseSvg icon="arrow-right-long" class="h-5 w-5 text-white" />
        </div>
      </div>

      <!-- Left side - Original -->
      <div class="flex flex-1 flex-col gap-3 p-2">
        <div class="flex flex-col items-center gap-2">
          <p class="text-center text-sm font-bold">Original Upload</p>
          <img
            :src="beforeImage"
            alt="Before artist edits"
            class="w-full max-w-[180px] object-cover"
          />
        </div>
        <div class="space-y-2">
          <p class="text-sm font-bold text-gray-800">Sample message to Artist:</p>
          <div
            v-for="(message, index) in sampleMessages"
            :key="message"
            :data-testid="`sample-message-${index}`"
            class="flex items-start gap-2"
          >
            <div
              class="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white"
            >
              {{ index + 1 }}
            </div>
            <span class="mb-0 text-sm leading-tight text-gray-600">{{ message }}</span>
          </div>
        </div>
      </div>

      <!-- Right side - Artist Edited -->
      <div class="flex flex-1 flex-col gap-3 rounded-lg bg-[#E3F1FF] p-2">
        <div class="flex flex-col items-center gap-2">
          <div class="flex items-center gap-2">
            <p class="text-center text-sm font-bold">Artist Edited</p>
            <span
              data-testid="artist-edits-free-badge"
              class="rounded-full bg-green-700 px-1.5 py-0.5 text-xs font-medium text-white"
            >
              Free
            </span>
          </div>
          <img
            :src="afterImage"
            alt="After artist edits"
            class="w-full max-w-[180px] object-cover"
          />
        </div>
        <div class="space-y-2">
          <p class="text-center text-sm font-bold text-gray-800">Artist Edits:</p>
          <div class="flex justify-center">
            <div class="flex flex-col gap-2">
              <div
                v-for="edit in artistEdits"
                :key="edit"
                :data-testid="`artist-edit-${edit}`"
                class="flex items-center gap-2"
              >
                <CheckCircleIcon class="size-6 shrink-0 text-green-700" />
                <span class="text-sm font-bold text-gray-800">{{ edit }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features section -->
    <div class="space-y-3">
      <p class="text-base font-bold">What else do you get with Artist Edit?</p>
      <div class="grid grid-cols-2 gap-2">
        <div
          v-for="feature in features"
          :key="feature"
          class="flex items-center gap-2 text-xs font-normal text-gray-900 lg:text-sm"
        >
          <CheckIcon class="size-6 shrink-0 text-green-700" />
          <span class="mb-0 text-sm leading-tight text-gray-600">{{ feature }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import CheckCircleIcon from "assets/icons/check-circle-broken.svg?vue";
import CheckIcon from "assets/icons/check.svg?vue";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "common/vue/components/UseSvg.vue";

defineOptions({ name: "ArtistEditsInformation" });

defineProps({
  isMobile: { type: Boolean, default: false }
});

defineEmits(["close"]);

const beforeImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-before.png", {
  w: 275,
  h: 275
});

const afterImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-after.png", {
  w: 275,
  h: 275
});

const sampleMessages = [
  'Add the text "#1 Fan" in white color onto the blue flag.',
  "Change the color of the dog's t-shirt from blue to red."
];

const artistEdits = ["Text Added", "Color Changed"];

const features = [
  "<24 hour turnaround",
  "Direct print, fast delivery",
  "Background removal",
  "Change color",
  "Add text"
];
</script>
